import React, { ChangeEvent, FC, FocusEvent, InputHTMLAttributes, ReactElement, useState } from 'react'
import Chip from '@material-ui/core/Chip'
import { InputAdornment, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { AutocompleteGetTagProps, AutocompleteRenderInputParams } from '@material-ui/lab'
import FormRow from '../../../../React/Common/Components/FormRow'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import type { SelectFilterOption } from './SelectFilter'

export type SelectFilterMultiProps = {
    /** Options shown in the dropdown; equality uses {@link SelectFilterOption.value}. */
    readonly options: readonly SelectFilterOption[]
    readonly label: string
    readonly icon: ReactElement
    /** Currently selected options (controlled). */
    readonly value: readonly SelectFilterOption[]
    /** Invoked with the new selection (possibly empty). */
    readonly onChange: (next: SelectFilterOption[]) => void
    readonly disabled?: boolean
    /**
     * When more than one option is selected, show this label as plain inline text instead of one chip per value.
     * Zero or one selected value keeps the default per-option chips.
     */
    readonly formatMultipleSelectionLabel?: (selectedCount: number) => string
}

/**
 * <p>Multi-select variant of {@link SelectFilter} for the admin filters grid: same outlined {@code TextField},
 * icon adornment, and {@link FormRow} layout.</p>
 *
 * <p>When nothing is selected, the outlined label is forced not to shrink so it sits inside the field (Material-UI
 * would otherwise shrink it whenever a start adornment is present). With selections, focus, or the listbox open, the
 * label shrinks to the notch like other outlined controls.</p>
 *
 * <p>The {@code FormRow} uses the {@code select-filter-multi} class so admin filter styles can offset the empty-state
 * label past the leading icon.</p>
 *
 * @param props - Options, label, icon, value, change handler, optional disabled flag, and optional collapsed
 * multi-select label formatter.
 * @returns Autocomplete multi wired like other admin list column filters.
 */
const primaryOptionLabel = (option: SelectFilterOption): string =>
    isStringNotEmpty(option?.label) ? option.label : String(option?.value ?? EMPTY_STRING)

export const SelectFilterMulti: FC<SelectFilterMultiProps> = ({
    options,
    label,
    icon,
    value,
    onChange,
    disabled,
    formatMultipleSelectionLabel
}: SelectFilterMultiProps): ReactElement => {
    const selected: SelectFilterOption[] = [...value]
    const [listOpen, setListOpen] = useState<boolean>(false)
    const [inputFocused, setInputFocused] = useState<boolean>(false)

    const shrinkOutlinedLabel: boolean = selected.length > 0 || inputFocused || listOpen

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            onOpen={(): void => setListOpen(true)}
            onClose={(): void => setListOpen(false)}
            onChange={(_: ChangeEvent<object>, val: SelectFilterOption[]): void => onChange(val)}
            options={[...options]}
            classes={{ popper: 'select-filter-popper' }}
            getOptionLabel={(option: SelectFilterOption): string => primaryOptionLabel(option)}
            getOptionSelected={(option: SelectFilterOption, candidate: SelectFilterOption): boolean =>
                option.value === candidate.value
            }
            value={selected}
            disabled={disabled ?? false}
            renderTags={(tagValue: SelectFilterOption[], getTagProps: AutocompleteGetTagProps): React.ReactNode => {
                if (isVarNotEmpty(formatMultipleSelectionLabel) && tagValue.length > 1) {
                    return (
                        <span key='select-filter-multi-collapsed' className='select-filter-multi-collapsed-label'>
                            {formatMultipleSelectionLabel(tagValue.length)}
                        </span>
                    )
                }

                return tagValue.map(
                    (option: SelectFilterOption, index: number): ReactElement => (
                        <Chip
                            {...getTagProps({ index })}
                            key={String(option.value)}
                            size='small'
                            label={primaryOptionLabel(option)}
                        />
                    )
                )
            }}
            renderInput={(params: AutocompleteRenderInputParams): ReactElement => {
                const baseInputProps: InputHTMLAttributes<HTMLInputElement> =
                    params.inputProps as InputHTMLAttributes<HTMLInputElement>

                return (
                    <FormRow className='select-filter-multi'>
                        <TextField
                            {...params}
                            size='small'
                            label={label}
                            variant='outlined'
                            InputLabelProps={{
                                ...params.InputLabelProps,
                                shrink: shrinkOutlinedLabel
                            }}
                            inputProps={{
                                ...baseInputProps,
                                onFocus: (event: FocusEvent<HTMLInputElement>): void => {
                                    baseInputProps.onFocus?.(event)
                                    setInputFocused(true)
                                },
                                onBlur: (event: FocusEvent<HTMLInputElement>): void => {
                                    baseInputProps.onBlur?.(event)
                                    setInputFocused(false)
                                }
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position='start'>{icon}</InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                )
                            }}
                        />
                    </FormRow>
                )
            }}
        />
    )
}
